import React, { useCallback, useMemo, useRef, useState } from 'react'
import { ErrorReportForm } from '@components/ErrorReport/ErrorReport'
import { ModalContainer } from '@components/Modals/ModalContainer'
import {
    DEFAULT_CONFIG,
    NodeAnimationContext,
} from '@components/NodeAnimation/NodeAnimationContext'
import { NodeAnimationLoader } from '@components/NodeAnimation/NodeAnimationLoader'
import { Panel } from '@components/Panel/Panel'
import { Box, IconButton } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { useStore } from 'store/store'
import { Figma } from 'ui/styles/palette'

import { useConnectionStatus } from './hooks/useConnectionStatus'
import { useNodeData } from './hooks/useNodeData'
import { NodeStatusPill } from './NodeStatusPill'

enum ModalType {
    ReportBug = 'report-bug',
}

export const NodeStatusPanel = () => {
    const darkMode = useStore((state) => state.getTheme() === 'dark')
    const { isTouch } = useDevice()
    const [modal, setModal] = useState<ModalType | undefined>(undefined)

    const backgroundColorString = useMemo(
        () => (darkMode ? Figma.DarkMode.Level3 : Figma.LightMode.Level3),
        [darkMode],
    )
    const { connectionStatus, nodeUrl } = useConnectionStatus()
    const nodeConnections = useNodeData()
    const config = useMemo(
        () => ({
            ...DEFAULT_CONFIG,
            animateNodes: false,
            nodeUrl,
            darkMode,
            backgroundColorString,
            nodeConnections,
        }),
        [backgroundColorString, darkMode, nodeConnections, nodeUrl],
    )

    const ref = useRef<HTMLDivElement>(null)

    const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const scrollTop = e.currentTarget.scrollTop
        ref.current?.style.setProperty('--scroll-top', `${Math.max(0, scrollTop - 50) / 250}`)
    }, [])

    const onBugReportClick = useCallback(() => {
        setModal(ModalType.ReportBug)
    }, [])

    const rightBarButton = isTouch ? (
        <IconButton icon="bug" background="none" onClick={onBugReportClick} />
    ) : undefined

    return (
        <Panel
            label="Towns Network"
            style={{ userSelect: 'none' }}
            rightBarButton={rightBarButton}
            onScroll={onScroll}
        >
            <Box centerContent ref={ref}>
                <Box width="300" height="300" />
                <Box
                    centerContent
                    width="300"
                    height="300"
                    position="absolute"
                    style={{
                        opacity: `calc(1 - var(--scroll-top) * 1)`,
                        transform: `translateY(
                            calc(var(--scroll-top) * -32px)
                        )`,
                    }}
                >
                    <NodeAnimationContext.Provider value={config}>
                        <NodeAnimationLoader minWidth="300" />
                    </NodeAnimationContext.Provider>
                </Box>
            </Box>
            <Box gap position="relative">
                {nodeConnections?.length ? (
                    nodeConnections.map((n) => (
                        <NodeStatusPill
                            nodeData={n}
                            key={n.id}
                            connectionStatus={n.nodeUrl === nodeUrl ? connectionStatus : undefined}
                            boxShadow={n.nodeUrl === nodeUrl ? 'panel' : undefined}
                        />
                    ))
                ) : (
                    <></>
                )}
            </Box>

            {modal === ModalType.ReportBug && (
                <ModalContainer touchTitle="Bug Report" onHide={() => setModal(undefined)}>
                    <Box padding gap>
                        <ErrorReportForm onHide={() => setModal(undefined)} />
                    </Box>
                </ModalContainer>
            )}
        </Panel>
    )
}
